import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SideDrawer, {
  FieldLabel,
  dateInputToISO,
  fieldSx,
  primaryButtonSx,
  todayISODate,
} from "components/SideDrawer";
import { useToast } from "components/Toast";
import { DateField } from "components/FormPickers";
import { getAddonList } from "services/vehicle";
import { Booking } from "services/manualBooking";
import {
  FlightFinance,
  addFlightAddon,
  formatNaira,
  getApiError,
} from "services/finance";
import { routeLabel } from "./BookingSummaryCard";

interface CatalogAddon {
  _id: string;
  name: string;
  price: number;
  description?: string;
}

const statusChipSx = (status?: string) => {
  const paid = (status || "").toLowerCase() === "paid";
  return {
    height: 18,
    fontSize: 11,
    borderRadius: "4px",
    bgcolor: paid ? "#ECFDF3" : "#FFFAEB",
    color: paid ? "#12B76A" : "#DC6803",
  };
};

const AddonsDrawer = ({
  open,
  onClose,
  booking,
  finance,
}: {
  open: boolean;
  onClose: () => void;
  booking: Booking;
  finance?: FlightFinance;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [view, setView] = useState<"list" | "add">("list");
  const [addonId, setAddonId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISODate());
  const [notes, setNotes] = useState("");

  const items: any[] = finance?.revenue?.addons?.items || [];
  const total = finance?.revenue?.addons?.amount || 0;

  const { data: catalogData, isLoading: catalogLoading } = useQuery({
    queryKey: ["addons"],
    queryFn: getAddonList,
    enabled: open && view === "add",
  });
  const catalog: CatalogAddon[] = catalogData?.data?.addons || [];

  const resetForm = () => {
    setAddonId("");
    setAmount("");
    setDate(todayISODate());
    setNotes("");
  };

  useEffect(() => {
    if (open) {
      setView("list");
      resetForm();
    }
  }, [open]);

  const handleAddonChange = (id: string) => {
    setAddonId(id);
    const selected = catalog.find((a) => a._id === id);
    if (selected) setAmount(String(selected.price ?? ""));
  };

  const mutation = useMutation({
    mutationFn: () =>
      addFlightAddon(booking.id, {
        addon_id: addonId,
        amount: amount !== "" ? Number(amount) : undefined,
        date: dateInputToISO(date),
        notes: notes.trim(),
      }),
    onSuccess: (res) => {
      showToast(res?.message || "Add-on added", "success");
      if (res?.data?.finance) {
        queryClient.setQueryData(["flightFinance", booking.id], {
          success: true,
          message: res.message,
          data: res.data.finance,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["flightFinance", booking.id],
      });
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      resetForm();
      setView("list");
    },
    onError: (error: any) =>
      showToast(getApiError(error, "Failed to add add-on"), "error"),
  });

  const addButton = (
    <Button
      variant="contained"
      size="small"
      onClick={() => setView("add")}
      sx={{
        bgcolor: "#0b1c12",
        textTransform: "none",
        borderRadius: "6px",
        "&:hover": { bgcolor: "#08150e" },
      }}
    >
      + Add Add-on
    </Button>
  );

  if (view === "add") {
    return (
      <SideDrawer open={open} onClose={onClose} title="Add Add-on">
        <Box
          bgcolor="#F9FAFB"
          border="1px solid #E4E7EC"
          borderRadius="8px"
          p="12px"
          display="flex"
          justifyContent="space-between"
          gap="8px"
          flexWrap="wrap"
        >
          <Typography fontSize={14} fontWeight={500} color="#101828">
            {routeLabel(booking)}
          </Typography>
          <Typography fontSize={13} color="#667085">
            PNR: {booking.pnr || "N/A"}
          </Typography>
        </Box>
        <Divider sx={{ my: "24px" }} />

        <Box display="flex" flexDirection="column" gap="16px">
          <Box>
            <FieldLabel>Add-on</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              sx={fieldSx}
              value={addonId}
              onChange={(e) => handleAddonChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                {catalogLoading ? "Loading add-ons..." : "Select an add-on"}
              </MenuItem>
              {catalog.map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  {a.name} · {formatNaira(a.price)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box>
            <FieldLabel>Amount</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              sx={fieldSx}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helperText="Defaults to the add-on price"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₦</InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <FieldLabel>Date</FieldLabel>
            <DateField value={date} onChange={setDate} disableFuture />
          </Box>
          <Box>
            <FieldLabel>Description</FieldLabel>
            <TextField
              fullWidth
              multiline
              rows={3}
              sx={fieldSx}
              placeholder="Add a short description of this service."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </Box>

        <Divider sx={{ my: "24px" }} />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap="12px">
          <Button
            variant="outlined"
            onClick={() => setView("list")}
            sx={{
              height: 44,
              borderRadius: "8px",
              textTransform: "none",
              color: "#344054",
              borderColor: "#D0D5DD",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ ...primaryButtonSx, height: 44 }}
            disabled={!addonId || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Adding..." : "Add Add-on"}
          </Button>
        </Box>
      </SideDrawer>
    );
  }

  return (
    <SideDrawer open={open} onClose={onClose} title="Add-ons">
      <Box
        bgcolor="#F9FAFB"
        border="1px solid #E4E7EC"
        borderRadius="8px"
        p="16px"
      >
        <Typography fontSize={15} fontWeight={500} color="#101828">
          {routeLabel(booking)}
        </Typography>
      </Box>
      <Box display="flex" gap="16px" mt="16px">
        <Box>
          <Typography fontSize={13} color="#667085">
            PNR
          </Typography>
          <Typography fontSize={15} color="#101828">
            {booking.pnr || "N/A"}
          </Typography>
        </Box>
        <Box>
          <Typography fontSize={13} color="#667085">
            Reference
          </Typography>
          <Typography fontSize={15} color="#101828">
            {booking.booking_id}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: "20px" }} />

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap="8px"
        >
          <Typography fontSize={16} fontWeight={500} color="#101828">
            Add-ons
          </Typography>
          {items.length > 0 && addButton}
        </Box>
        <Typography fontSize={12} color="#667085">
          Manage additional services added to this booking.
        </Typography>
      </Box>

      {items.length === 0 ? (
        <Box
          mt="24px"
          border="1px solid #E4E7EC"
          bgcolor="#FCFCFD"
          borderRadius="12px"
          py="48px"
          px="16px"
          textAlign="center"
        >
          <Typography fontSize={15} fontWeight={500} color="#101828">
            No add-ons added
          </Typography>
          <Typography fontSize={13} color="#667085" mb="16px">
            Add an additional service or charge to this booking.
          </Typography>
          {addButton}
        </Box>
      ) : (
        <>
          <Box mt="16px">
            {items.map((item, index) => (
              <Box
                key={item.id || item._id || index}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py="12px"
                borderBottom="1px solid #E4E7EC"
              >
                <Box>
                  <Typography fontSize={14} color="#101828">
                    {item.name || item.label || item.description || "Add-on"}
                  </Typography>
                  <Chip
                    size="small"
                    label={item.status || "Paid"}
                    sx={statusChipSx(item.status || "Paid")}
                  />
                </Box>
                <Typography fontSize={14} fontWeight={600} color="#101828">
                  {formatNaira(item.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            mt="24px"
            bgcolor="#ECFDF3"
            border="1px solid #D1FADF"
            borderRadius="8px"
            p="16px"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontSize={14} color="#101828">
              Total Add-ons:
            </Typography>
            <Typography fontSize={20} fontWeight={700} color="#101828">
              {formatNaira(total)}
            </Typography>
          </Box>
        </>
      )}
    </SideDrawer>
  );
};

export default AddonsDrawer;
